import type { DestructuredScenario } from "@/composables/useDestructureScenario";
import { useScenarioValidator } from "@/composables/validation/scenarioValidator";
import type { DataSet, NumericPoint } from "@incytestudios/ddv";
import { computed } from "vue";
import { isFinitePoint, isNaturalPoint } from "@/libs/charting";
import type { PredictiveCalcs, PumpTornadoChartContext } from ".";
import { convert } from "@/libs/units";
import type { OperatingPoint } from "@/components/types";

const calculateAnalysisPoints = (
  context: PumpTornadoChartContext,
  destructuredScenario: DestructuredScenario,
  predictedCalcs: PredictiveCalcs
): {
  tdh: NumericPoint[];
  bhp: NumericPoint[];
} => {
  const data = Object.values(predictedCalcs)
    .sort(
      (a, z) =>
        a.hypotheticalCalcs.pumpHz.value - z.hypotheticalCalcs.pumpHz.value
    )
    .map(c => {
      const { errors } = useScenarioValidator(
        destructuredScenario,
        c.hypotheticalCalcs
      );
      const predictionStatus = c.predictedFlowRateDataSet.status;
      // Errors will cause the chart not draw the point
      const predictionErrors = computed(() =>
        errors.value.filter(e =>
          [
            "pip_range",
            "pump_outside_graphing_range",
            "target_rate_above_max_flow_rate"
          ].includes(e.name)
        )
      );
      if (
        predictionErrors.value.length > 0 ||
        predictionStatus !== "result-found"
      ) {
        return {
          tdh: { x: NaN, y: NaN },
          bhp: { x: NaN, y: NaN }
        };
      } else {
        return {
          tdh: {
            x: c.hypotheticalCalcs.pumpCalculations.aggregate
              .dischargeFlowRateSTBD.value,
            y: context.isScenarioEspShore
              ? c.hypotheticalCalcs.pumpCalculations.aggregate
                  .requiredTotalDynamicHeadFt.value
              : c.hypotheticalCalcs.pumpCalculations.aggregate
                  .differentialPressurePSIA.value
          },
          bhp: {
            x: c.hypotheticalCalcs.pumpCalculations.aggregate
              .dischargeFlowRateSTBD.value,
            y: c.hypotheticalCalcs.pumpCalculations.aggregate
              .requiredPumpBrakeHP.value
          }
        };
      }
    });
  return data.reduce(
    (a, p) => {
      return {
        tdh: [...a.tdh, p.tdh],
        bhp: [...a.bhp, p.bhp]
      };
    },
    {
      tdh: [] as NumericPoint[],
      bhp: [] as NumericPoint[]
    }
  );
};

export const getAnalysisDataSets = (
  context: PumpTornadoChartContext,
  predictedCalcs: PredictiveCalcs,
  headDischargeOperatingPoint: OperatingPoint,
  bhpPumpDischargeOperatingPoint: OperatingPoint,
  destructuredScenario?: DestructuredScenario
): {
  tdh: DataSet;
  bhp: DataSet;
} => {
  if (!destructuredScenario)
    return { tdh: { points: [] }, bhp: { points: [] } };
  const {
    baseXUnit,
    targetXUnit,
    targetXUnitLabel,
    baseTdhYUnit,
    targetTdhYUnit,
    targetTdhYUnitLabel,
    baseBhpYUnit,
    targetBhpYUnit,
    targetBhpYUnitLabel,
    isScenarioEspShore
  } = context;
  const analysisPoints = calculateAnalysisPoints(
    context,
    destructuredScenario,
    predictedCalcs
  );
  // If ESP then add the real pump operating points to the analysis lines
  if (isScenarioEspShore) {
    const tdhDischargeOperatingPoint = headDischargeOperatingPoint;
    // Add discharge point to TDH since we use the discharge rates there
    if (tdhDischargeOperatingPoint) {
      const tdhDischargePoint =
        tdhDischargeOperatingPoint.rawPoint || tdhDischargeOperatingPoint.point;
      const hasDischargeX = analysisPoints.tdh.some(
        point => point.x === tdhDischargePoint.x
      );
      if (!hasDischargeX) {
        analysisPoints.tdh.push(tdhDischargePoint);
      }
    }
    analysisPoints.tdh.sort((a, b) => a.x - b.x);

    // Add intake point to BHP since we use the intake rates there
    // Remember, the OP
    const bhpDischargeOperatingPoint = bhpPumpDischargeOperatingPoint;
    if (bhpDischargeOperatingPoint) {
      const bhpDischargePoint =
        bhpDischargeOperatingPoint.rawPoint || bhpDischargeOperatingPoint.point;
      const hasIntakeX = analysisPoints.bhp.some(
        point => point.x === bhpDischargePoint.x
      );
      if (!hasIntakeX) {
        analysisPoints.bhp.push(bhpDischargePoint);
      }
    }
    analysisPoints.bhp.sort((a, b) => a.x - b.x);
  }

  const title = isScenarioEspShore ? "Required TDH" : "Required Pressure";
  return {
    tdh: {
      title,
      xAxisLabel: `(${targetXUnitLabel})`,
      yAxisLabel: `(${targetTdhYUnitLabel})`,
      color: "var(--plotColor3)",
      curveType: "straight",
      points: analysisPoints.tdh
        .filter(isFinitePoint)
        .filter(isNaturalPoint)

        .map(p => ({
          ...p,
          x: convert(p.x, baseXUnit, targetXUnit),
          y: convert(p.y, baseTdhYUnit, targetTdhYUnit)
        }))
    },
    bhp: {
      xAxisLabel: `(${targetXUnitLabel})`,
      yAxisLabel: `(${targetBhpYUnitLabel})`,
      color: "var(--plotColor1)",
      curveType: "straight",
      points: analysisPoints.bhp
        .filter(isFinitePoint)
        .filter(isNaturalPoint)
        .map(p => {
          return {
            ...p,
            x: convert(p.x, baseXUnit, targetXUnit),
            y: convert(p.y, baseBhpYUnit, targetBhpYUnit)
          };
        })
    }
  };
};
